import type {
  AccountGradingRecording,
  GradingId,
} from "@repo/app-types/entities/teammatePerformanceSummary";
import type {
  Paragraph,
  QuoteParagraph,
} from "@repo/app-types/helpers/gradeValidationHelpers";
import { UNKNOWN_CONTACT_DISPLAY_NAME } from "@repo/app-types/helpers/utilities";
import {
  SlideoverDialog,
  SlideoverDialogContentGray,
  SlideoverDialogHeader,
  SlideoverDialogTitle,
} from "@repo/ui/Slideover/slideoverDialog";
import { FullAbsoluteTimestamp } from "@repo/ui/timestamps/fullAbsoluteTimestamp";
import { IconUser } from "@tabler/icons-react";
import { useMemo, type FC, type ReactNode } from "react";
import { twMerge } from "tailwind-merge";
import { AudioPlayerProvider } from "../../components/audioPlayer/provider.js";
import { PH_MASK_CLASS } from "../analytics/sessionRecordingMasking.js";
import { ParagraphsContainer } from "../teammatePerformance/paragraphsContainer.js";
import { TranscriptViewerPane } from "./transcriptViewerPane.js";
import { useMatchQuoteToTranscript } from "./useMatchQuoteToTranscript.js";

export interface MatchAnalysisMetadataEntry {
  label: string;
  value: ReactNode;
}

export interface MatchAnalysisSubtitle {
  contactName: string | null;
  contactLinkUrl?: string;
  recordedAt: string | null;
}

export interface MatchAnalysisSlideoverScaffoldProps {
  title: string;
  subtitle?: MatchAnalysisSubtitle;
  rightAction?: ReactNode;
  teammateName?: string;
  summaryText?: string;
  metadataEntries?: readonly MatchAnalysisMetadataEntry[];
  analysis: readonly Paragraph[];
  gradingId: GradingId;
  recordings: readonly AccountGradingRecording[];
}

/**
 * Shared two-pane slideover used by both the signal-match and the
 * topic-match transcript views. Header surfaces the entity name,
 * contact (optionally linked to the grading page), and recording
 * timestamp. Left pane stacks Summary + Analysis — Summary holds a
 * prose blurb and any number of metadata rows (signal metadata fields,
 * or emotion, etc.). Right pane is the transcript viewer.
 *
 * Metadata rows take rendered `value` nodes rather than raw strings so
 * callers can drop in domain pills (emotion pills, etc.) without the
 * scaffold needing to know those types.
 */
export const MatchAnalysisSlideoverScaffold: FC<
  MatchAnalysisSlideoverScaffoldProps
> = (props) => {
  const quoteParagraphs = useMemo(
    (): readonly QuoteParagraph[] =>
      props.analysis.filter((p): p is QuoteParagraph => p.type === "quote"),
    [props.analysis],
  );

  const {
    sortedRecordings,
    quoteMatches,
    handleQuotePress,
    transcriptRef,
    transcriptContainerRef,
  } = useMatchQuoteToTranscript({
    recordings: props.recordings,
    quoteParagraphs,
  });

  const metadataEntries = props.metadataEntries ?? [];
  const hasSummarySection =
    Boolean(props.summaryText) || metadataEntries.length > 0;

  return (
    <SlideoverDialog label={props.title}>
      <SlideoverDialogHeader isLoading={false} right={props.rightAction}>
        <div className="min-w-0">
          <SlideoverDialogTitle>{props.title}</SlideoverDialogTitle>
          {props.subtitle ? (
            <p className="truncate text-xs text-muted-foreground">
              {props.subtitle.contactLinkUrl ? (
                <a
                  href={props.subtitle.contactLinkUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-medium text-indigo-600 hover:text-indigo-500"
                >
                  {props.subtitle.contactName ?? UNKNOWN_CONTACT_DISPLAY_NAME}
                </a>
              ) : (
                <span className="font-medium text-foreground">
                  {props.subtitle.contactName ?? UNKNOWN_CONTACT_DISPLAY_NAME}
                </span>
              )}
              {props.teammateName ? (
                <span className={PH_MASK_CLASS}>
                  {` with ${props.teammateName}`}
                </span>
              ) : null}
              {props.subtitle.recordedAt ? (
                <>
                  <span className="mx-1.5 text-gray-400">&middot;</span>
                  <FullAbsoluteTimestamp
                    timestamp={props.subtitle.recordedAt}
                  />
                </>
              ) : null}
            </p>
          ) : null}
        </div>
      </SlideoverDialogHeader>

      <SlideoverDialogContentGray>
        <AudioPlayerProvider>
          <div className="grid h-full grid-cols-[minmax(0,1fr)_minmax(0,1fr)] grid-rows-[minmax(0,1fr)_auto]">
            <div className="relative space-y-4 overflow-y-auto border-r border-border-light p-4">
              {props.teammateName ? (
                <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                  <IconUser className="size-4 shrink-0" />
                  <span>{props.teammateName}</span>
                </div>
              ) : null}
              <div className="rounded-lg border border-border-light bg-white">
                {hasSummarySection ? (
                  <div className="space-y-2 p-4">
                    <h3 className="mb-1 text-xs font-semibold text-muted-foreground">
                      Summary
                    </h3>
                    {props.summaryText ? (
                      <p className="text-sm text-foreground">
                        {props.summaryText}
                      </p>
                    ) : null}
                    {metadataEntries.length > 0 ? (
                      <dl className="space-y-1 text-xs">
                        {metadataEntries.map((entry) => (
                          <div key={entry.label} className="flex gap-2">
                            <dt className="font-medium text-gray-400">
                              {entry.label}
                            </dt>
                            <dd className="text-gray-600">{entry.value}</dd>
                          </div>
                        ))}
                      </dl>
                    ) : null}
                  </div>
                ) : null}

                {props.analysis.length > 0 ? (
                  <div
                    className={twMerge(
                      "p-4",
                      hasSummarySection && "border-t border-gray-100",
                    )}
                  >
                    <h3 className="mb-2 text-xs font-semibold text-muted-foreground">
                      Analysis
                    </h3>
                    <ParagraphsContainer
                      recordings={sortedRecordings}
                      paragraphs={props.analysis}
                      hasCompleted={true}
                      quoteMatches={quoteMatches}
                      onQuotePress={handleQuotePress}
                    />
                  </div>
                ) : null}
              </div>
            </div>

            <TranscriptViewerPane
              gradingId={props.gradingId}
              sortedRecordings={sortedRecordings}
              quoteMatches={quoteMatches}
              transcriptRef={transcriptRef}
              transcriptContainerRef={transcriptContainerRef}
            />
          </div>
        </AudioPlayerProvider>
      </SlideoverDialogContentGray>
    </SlideoverDialog>
  );
};
